// ─── Types partagés du chat agents (story 5.5) ───────────────────────────────
// Contrat SSE (backend) + modèle de données frontend des messages agents.

// mirror of backend/src/routes/chat.ts ChatSSEEvent — keep in sync
export type ActionCardTier = 2 | 3

// Story 7.2 — détail dépliable d'une carte (destinataires, objet + message).
export type ActionCardPreview = { label: string; items: string[]; text?: string }

// Règle PARTAGÉE avec `toPreview` (backend/src/routes/chat.ts) — garder en phase :
// `label` texte, `items` tableau de textes, `text` absent (undefined) ou texte.
// Toute autre forme (y compris `text: null` ou non-texte) → preview entière ignorée.
export function isActionCardPreview(value: unknown): value is ActionCardPreview {
  if (typeof value !== 'object' || value === null) return false
  const { label, items, text } = value as { label?: unknown; items?: unknown; text?: unknown }
  return (
    typeof label === 'string' &&
    Array.isArray(items) &&
    items.every((i) => typeof i === 'string') &&
    (text === undefined || typeof text === 'string')
  )
}

export type ChatSSEEvent =
  | { type: 'content'; content: string }
  // `toolCallId` (story 6.2) : toujours émis par le backend, optionnel ici pour
  // rester tolérant (repli FIFO par nom quand absent).
  | { type: 'tool_call'; toolCallId?: string; name: string; arguments: unknown }
  | { type: 'tool_result'; toolCallId?: string; name: string; result: unknown }
  // Story 7.1 : `toolCallId` + `actionId` (cible de /actions/:actionId/confirm).
  | {
      type: 'action_card'
      tier: ActionCardTier
      title: string
      description: string
      name: string
      toolCallId?: string
      actionId?: string
      // Story 7.2 — optionnels : libellé du bouton principal (« Envoyer ») et
      // détail dépliable « Voir avant d'envoyer ».
      primaryLabel?: string
      preview?: ActionCardPreview
    }
  | { type: 'suggestion_pills'; suggestions: string[] }
  | { type: 'error'; error: string }

export const CHAT_SSE_EVENT_TYPES: readonly ChatSSEEvent['type'][] = [
  'content',
  'tool_call',
  'tool_result',
  'action_card',
  'suggestion_pills',
  'error',
] as const

// Limite alignée sur le backend (`MAX_MESSAGE_LENGTH` de chat.ts).
export const CHAT_MESSAGE_MAX_LENGTH = 8000

// ─── Réponses REST /api/chat ─────────────────────────────────────────────────
export type ChatConversationSummary = {
  id: string
  title: string | null
  createdAt: string
  updatedAt: string
}

export type ChatConversationCreated = {
  id: string
  title: string | null
  createdAt: string
}

// LLMToolCall côté backend : `arguments` est une string JSON.
export type PersistedToolCall = { id: string; name: string; arguments: string }

// Story 7.1 — durée de vie d'une action Tier 2 en attente (miroir de
// `PENDING_ACTION_TTL_MS`, backend/src/tools/pendingActions.ts).
export const PENDING_ACTION_TTL_MS = 15 * 60_000

// Story 7.1 — issue persistée d'une action Tier 2 (confirm / cancel).
export type ActionOutcomePayload = {
  state: 'executed' | 'failed' | 'cancelled'
  resultText?: string
  error?: string
  navigate?: ActionNavigateLink
  at?: string
}

// Story 7.1 (PO Q3) — lien proposé sur une carte exécutée (`data.navigate` du
// résultat, ex. createCampaign → vue Campagnes pour ajouter des fournisseurs).
export type ActionNavigateLink = { route: string; label: string }

export function isActionNavigateLink(value: unknown): value is ActionNavigateLink {
  if (typeof value !== 'object' || value === null) return false
  const { route, label } = value as { route?: unknown; label?: unknown }
  return typeof route === 'string' && typeof label === 'string'
}

// Message affiché quand le serveur ne connaît plus l'action (TTL dépassé ou
// redémarrage — actions en mémoire, décision PO Q1).
export const ACTION_EXPIRED_MESSAGE = 'Action expirée, redemandez-la au chat'

// ToolResult côté backend (tools/types.ts).
export type ToolResultPayload = {
  success: boolean
  data?: unknown
  error?: string
  confirmationTier?: 1 | 2 | 3
  confirmationMessage?: string
  confirmationDetail?: string
  pendingActionId?: string
  // Story 7.2 — champs propres à la carte.
  confirmationPrimaryLabel?: string
  confirmationPreview?: ActionCardPreview
}

// Réponse de POST /api/chat/:id/actions/:actionId/confirm.
export type ChatActionConfirmResponse = {
  state: 'executed' | 'failed'
  result: { success: boolean; data?: unknown; error?: string }
}

export type PersistedToolResult = { toolCallId: string; result: unknown; outcome?: ActionOutcomePayload }

export type PersistedChatMessage = {
  id: string
  role: string
  content: string
  toolCalls: PersistedToolCall[] | null
  toolResults: PersistedToolResult[] | null
  createdAt: string
}

// ─── Modèle frontend ─────────────────────────────────────────────────────────
export type ToolInvocationStatus = 'running' | 'success' | 'error'

export type ToolInvocation = {
  id: string
  name: string
  args: unknown
  status: ToolInvocationStatus
  result?: unknown
  errorMessage?: string
  startedAt: number
}

export type ActionCardState = 'proposed' | 'confirmed' | 'executed' | 'cancelled' | 'failed' | 'expired'

export type ActionCardData = {
  id: string
  tier: ActionCardTier
  title: string
  description: string
  name: string
  state: ActionCardState
  readOnly?: boolean
  // Story 7.1 — protocole de confirmation réel.
  actionId?: string
  toolCallId?: string
  resultText?: string
  errorText?: string
  // Fenêtre d'annulation (5 s) en cours après « Confirmer ».
  undoSecondsLeft?: number
  // Lien proposé une fois l'action exécutée (PO Q3).
  navigate?: ActionNavigateLink
  // Story 7.2 — bouton principal (« Envoyer ») et détail dépliable.
  primaryLabel?: string
  preview?: ActionCardPreview
}

export type AgentChatMessageStatus = 'sending' | 'sent' | 'streaming' | 'done' | 'error'

export type AgentChatMessage = {
  id: string
  role: 'user' | 'assistant'
  content: string
  createdAt: string
  status: AgentChatMessageStatus
  toolInvocations?: ToolInvocation[]
  actionCards?: ActionCardData[]
  errorMessage?: string
}

export type ChatStreamStatus = 'idle' | 'submitted' | 'streaming' | 'error'

// Générateur d'id local (messages optimistes, invocations live).
export function createLocalId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `local-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

// Garde de type pour un ToolResult (payload `unknown` venant du flux / DB).
export function isToolResultPayload(value: unknown): value is ToolResultPayload {
  return (
    typeof value === 'object' &&
    value !== null &&
    'success' in value &&
    typeof (value as { success: unknown }).success === 'boolean'
  )
}
